import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'
import Modal from 'react-bootstrap-modal'
import DateInputWrapper from '../open-game-form/date-input-wrapper'
import SelectInputWrapper from '../open-game-form/select-input-wrapper'
import Fields from '../open-game-form/fields'
import FlashAlert from '../flash-alert'
import PlayerSelector from '../player-selector'
import AdditionalGames from '../open-game-form/additional-games'
import Recurring from '../open-game-form/recurring'
import { ajaxGet, postForm } from '../../ajax'
import CategorySelector from '../category-selector'
import moment from 'moment'

function GameForm({payload, onChangePayload, onCreate, onLoading}) {
  const [baseField, setBaseField] = useState(null)
  const [master, setMaster] = useState({})
  const [category, setCategory] = useState(null)
  const [alert, setAlert] = useState(null)
  const [showRecurring, setShowRecurring] = useState(false)
  const [subGames, setSubGames] = useState([])
  const [endDate, setEndDate] = useState(null)
  const [timeError, setTimeError] = useState(null)
  const [timeToError, setTimeToError] = useState(null)
  const [isLoading, setLoading] = useState(false)
  const showTo = payload.duration == 'custom'

  const { register, unregister, setError, clearErrors, getValues, setValue, handleSubmit, formState: { errors } } = useForm()

  useEffect(() => {
    if (payload) initForm(payload.form)
  }, [])

  const loadData = (params) => {
    onLoading(true)
    ajaxGet(`/admins/reservations/new.json?` + new URLSearchParams(params))
    .then(response => { if (response.ok) return response.json() })
    .then((json) => {
      onChangePayload(json)
      initForm(json.form)
      onLoading(false)
    })
  }

  const initForm = (data) => {
    for (const [key, value] of Object.entries(data)) { setValue(key, value) }
    setBaseField(data.field_id)
    setTimeError(null)
    setTimeToError(null)
  }
  
  const prepareFormData = (data) => {
    let formData = new FormData()
    formData.append('game[duration]', payload.duration)
    formData.append('game[times][]', payload.time)
    for (const key in data) {
      if (data[key] == undefined) continue
      if (key == 'times')
        data[key].forEach(v => v && formData.append('game[times][]', v))
      else
        formData.append(`game[${key}]`, data[key]) 
    }
    if (data.interval) formData.append('game[end_date]', endDate)
    if (showTo) formData.append('game[duration_end_time]', payload.time_to)
    if (category) formData.append('game[category_id]', category)
    if (master) setMasterData(formData)
    formData.append('game[field_ids][]', baseField)
    subGames.forEach(game => formData.append('game[field_ids][]', game.field))
    if (data.cost != payload.cost) formData.set('game[cost]', payload.cost)
    return formData
  }
  
  const setMasterData = (formData) => {
    if (master.id) formData.append('game[master_id]', master.id)
    if (master.firstName) formData.append('game[player_first_name]', master.firstName)
    if (master.lastName) formData.append('game[player_last_name]', master.lastName)
    if (master.phoneNumber) formData.append('game[player_phone]', master.phoneNumber)
    if (master.countryCode) formData.append('game[player_country_code]', master.countryCode)
    if (master.email) formData.append('game[player_email]', master.email)
  }

  const onSubmit = (data) => {
    if (!validateTime()) return
    if (showTo && !validateTimeTo()) return
    if (!validateEndDate()) return
    if (!validateMaster()) return

    setLoading(true)
    const formData = prepareFormData(data)
    postForm(`/admins/reservations.json?`, formData)
    .then(response => { return response.json() })
    .then((json) => {
      setLoading(false)
      if (json.alert)
        setAlert(json.alert)
      else
        onCreate()
    })
  }

  const validateTime = () => {
    if (payload.time)
      return true
    else {
      setTimeError({message: requiredMsg})
      return false
    }
  }

  const validateTimeTo = (value) => {
    if (!payload.time_to || payload.time >= payload.time_to) {
      setTimeToError({message: 'Must be > From'})
      return false
    }
    return true
  }

  const validateEndDate = () => {
    if (!showRecurring) return true

    var error = {}
    const interval = moment(new Date(endDate)).diff(new Date(baseDate), 'months', true)
    if (interval <= 0) error = { type: 'manual', message: 'Invalid interval'}
    if (interval > 12) error = { type: 'manual', message: 'Interval limit is 12 months'}
    if (!_.isEmpty(error)) {
      setError('end_date', error)
      return false
    }
    return true
  }

  const validateMaster = () => {
    if (_.isEmpty(master) || master.id) return true

    if (!master.phoneNumber) {
      setAlert({type: 'alert', text: "Phone number can't be blank"})
      return false
    }
    return true
  }

  const addSubGame = () => {
    setSubGames([...subGames, {field: baseField, date: payload.date, hours: payload.hours}])
  }

  const removeSubGame = (index) => {
    const newSubGames = [...subGames.slice(0, index), ...subGames.slice(index + 1, subGames.length)]
    setSubGames(newSubGames)
  }

  const changeSubGame = (index, params) => {
    const newSubGames = [ 
      ...subGames.slice(0, index), 
      {...subGames[index], ...params },
      ...subGames.slice(index + 1, subGames.length) ]
    setSubGames(newSubGames)
  }

  const changeEndDate = (date) => {
    setEndDate(date)
    clearErrors('end_date')
  }

  const changeMaster = (data) => {
    setMaster({...master, ...data})
  }

  const changeCategory = (value) => {
    setCategory(value)
  }

  const changeLocation = (locationId) => {
    const formParams = { 'game[date]': payload.date }
    if (locationId) formParams['game[location_id]'] = locationId
    loadData(formParams)
    setSubGames([])
  }

  const changeGameOptions = (params) => {
    const locationId = getValues('location_id')
    const formParams = {
      'game[duration]': payload.duration,
      'game[date]': payload.date,
      'game[times][]': payload.time,
    }
    if (locationId) {
      formParams['game[location_id]'] = locationId
      formParams['game[field_ids][]'] = baseField
    }
    if (showTo) formParams['game[duration_end_time]'] = payload.time_to
    loadData({...formParams, ...params})
  }

  const requiredMsg = 'Is required'
  
  if (!payload) return null
  const baseDate = payload.date

  return <div className='game-form'>
    { alert && <FlashAlert text={alert.text} type={alert.type} html={alert.html} constant centred onHide={() => setAlert(null)} />}
    { isLoading && <div className='animated-spinner' /> }
    <form className='form-horizontal usual-game-form' onSubmit={handleSubmit(onSubmit)}>
      <div className='row' style={{marginTop: '8px'}}>
        <div className='col-sm-9'>
          <PlayerSelector onChange={changeMaster} />
          {errors.master && <div className='error_container'>{errors.master.message}</div>}
        </div>
        <div className='col-sm-3'>
          <CategorySelector 
            categoryId={category}
            categories={payload.categories}
            onChange={changeCategory}
          />
        </div>
      </div><br />

      <div className='row'>
        <div className={'new-game-date ' + (payload.has_durations ? 'col-xs-6' : 'col-xs-9')}>
          <DateInputWrapper
            label='Date'
            onChange={(date) => changeGameOptions({'game[date]': (date || moment()).format('MM/DD/YYYY')})}
            date={moment(new Date(baseDate))}
          />
        </div>
        { payload.has_durations &&
          <div className='col-sm-2 new-game-duration'>
            <SelectInputWrapper 
              label='Duration'
              value={payload.duration}
              options={payload.durations}
              onChange={(e) => changeGameOptions({'game[duration]': e.target.value})}
            />
          </div>
        }
        <div className='col-sm-2 new-game-time'>
          <SelectInputWrapper
            label='From'
            selectClassName='time-select'
            value={payload.time}
            options={payload.hours}
            error={timeError}
            onChange={(e) => changeGameOptions({'game[times][]': e.target.value})}
          />
        </div>
        { showTo && 
          <div className='col-sm-2 new-game-time-to'>
            <SelectInputWrapper
              label='To'
              selectClassName='time-select'
              value={payload.time_to}
              options={payload.hours}
              error={timeToError}
              onChange={(e) => changeGameOptions({'game[duration_end_time]': e.target.value})}
            />
          </div>
        }
      </div>
      <div className='row'>
        { payload.locations && payload.locations.length > 1 && 
          <div className='col-sm-2'>
            <SelectInputWrapper 
              {...register('location_id')} 
              label='Location' 
              options={payload.locations}
              onChange={(e) => {
                changeLocation(e.target.value)
                setValue('location_id', e.target.value)
              }}
            />
          </div>
        }
      </div>

      { payload.fields && baseField &&
        <Fields 
          selected={baseField}
          fields={payload.fields} 
          fieldIndex={0}
          onChangeField={(value) => changeGameOptions({'game[field_ids][]': value})}
        /> 
      }

      { baseField  && 
        <AdditionalGames
          unregister={unregister}
          register={register}
          errors={errors}
          getValues={getValues}
          setValue={setValue}
          locationId={getValues('location_id')}
          fields={payload.fields}
          subGames={subGames}
          onAdd={addSubGame}
          onRemove={removeSubGame}
          onChange={changeSubGame}
          onLoading={onLoading}
        />
      }

      <div className='row'>
        <div className='col-sm-12'>
          <a className='btn btn-link clickable recurrings' onClick={() => setShowRecurring(!showRecurring)}>
            <i className='fa fa-lock'></i>
            &nbsp;Recurring Reservations
          </a>
        </div>
      </div>

      { showRecurring &&
        <Recurring 
          register={register}
          errors={errors}
          unregister={unregister}
          setValue={setValue}
          intervals={payload.intervals}
          baseDate={baseDate}
          endDate={endDate}
          onDateChange={changeEndDate}
        />
      }

      <div className='row'>
        <div className='col-sm-12'>
          <label className='control-label send-reservation-notification-checkbox'>
            <input 
              type='checkbox' 
              {...register('notify_player')}
              onChange={(e) => setValue('notify_player', e.target.checked)} />
            &nbsp;Send text confirmation to client
          </label>
        </div>
      </div>

      <Modal.Footer>
        { alert && <FlashAlert text={alert.text} type={alert.type} html={alert.html} constant onHide={() => setAlert(null)} />}
        <input type='submit' name='commit' value='Create Reservations' className='btn btn-primary create-game' />
      </Modal.Footer>
    </form>
  </div>
}

GameForm.propTypes = {
  payload: PropTypes.object.isRequired,
  onChangePayload: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired
}

export default GameForm
